import { useMemo } from 'react';
import { LockKeyhole } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { suggestEmailCorrection } from '../../shared/email.js';
import { recordFunnelEvent } from '../api.js';
import { AppShell } from '../components/AppShell.js';
import { Button, Field, Input, Notice } from '../components/ui.js';
import { copy } from '../i18n.js';
import { useDraft } from '../state/DraftContext.js';

export function ContactPage() {
  const { draft, update } = useDraft();
  const navigate = useNavigate();
  if (!draft) return null;
  const t = copy[draft.language];
  const suggestion = useMemo(
    () => suggestEmailCorrection(draft.contact.email),
    [draft.contact.email],
  );
  const valid = Boolean(
    draft.contact.firstName.trim() &&
    draft.contact.lastName.trim() &&
    /^\S+@\S+\.\S+$/.test(draft.contact.email) &&
    (draft.contact.preferredChannel !== 'PHONE' || draft.contact.phone.trim()) &&
    draft.legal.tosAccepted &&
    draft.legal.privacyAccepted,
  );
  const patchContact = (patch: Partial<typeof draft.contact>) =>
    update((current) => ({ ...current, contact: { ...current.contact, ...patch } }));
  return (
    <AppShell language={draft.language} step={4}>
      <header className="page-heading">
        <h1>{t.contact.title}</h1>
        <p>{t.contact.lead}</p>
      </header>
      <section className="flow-card contact-card">
        <div className="field-grid two">
          <Field label={t.contact.firstName}>
            <Input
              autoComplete="given-name"
              value={draft.contact.firstName}
              onChange={(event) => patchContact({ firstName: event.target.value })}
            />
          </Field>
          <Field label={t.contact.lastName}>
            <Input
              autoComplete="family-name"
              value={draft.contact.lastName}
              onChange={(event) => patchContact({ lastName: event.target.value })}
            />
          </Field>
        </div>
        <Field label={t.contact.email}>
          <Input
            type="email"
            autoComplete="email"
            value={draft.contact.email}
            onChange={(event) => patchContact({ email: event.target.value })}
          />
        </Field>
        {suggestion ? (
          <button
            className="email-suggestion"
            type="button"
            onClick={() => patchContact({ email: suggestion })}
          >
            {t.contact.suggestion} <strong>{suggestion}</strong>?
          </button>
        ) : null}
        <Field label={t.contact.phone}>
          <Input
            type="tel"
            autoComplete="tel"
            value={draft.contact.phone}
            onChange={(event) => patchContact({ phone: event.target.value })}
          />
        </Field>
        <fieldset className="channel-field">
          <legend>{t.contact.preferred}</legend>
          <label>
            <input
              type="radio"
              checked={draft.contact.preferredChannel === 'EMAIL'}
              onChange={() => patchContact({ preferredChannel: 'EMAIL' })}
            />
            {t.contact.emailChannel}
          </label>
          <label>
            <input
              type="radio"
              checked={draft.contact.preferredChannel === 'PHONE'}
              onChange={() => patchContact({ preferredChannel: 'PHONE' })}
            />
            {t.contact.phoneChannel}
          </label>
        </fieldset>
        <div className="legal-checks">
          <label>
            <input
              type="checkbox"
              checked={draft.legal.tosAccepted}
              onChange={(event) =>
                update((current) => ({
                  ...current,
                  legal: { ...current.legal, tosAccepted: event.target.checked },
                }))
              }
            />
            <span>
              I accept the{' '}
              <Link to="/legal/terms" target="_blank" onClick={(event) => event.stopPropagation()}>
                Refindi terms
              </Link>
              .
            </span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={draft.legal.privacyAccepted}
              onChange={(event) =>
                update((current) => ({
                  ...current,
                  legal: { ...current.legal, privacyAccepted: event.target.checked },
                }))
              }
            />
            <span>
              I have read the{' '}
              <Link
                to="/legal/privacy"
                target="_blank"
                onClick={(event) => event.stopPropagation()}
              >
                privacy notice
              </Link>
              , including OpenAI processing.
            </span>
          </label>
        </div>
      </section>
      <Notice tone="info">
        <LockKeyhole size={19} />
        Contact details are never public.
      </Notice>
      <div className="flow-actions">
        <Button variant="secondary" onClick={() => navigate('/report/proof')}>
          {t.common.back}
        </Button>
        <Button
          disabled={!valid}
          onClick={() => {
            recordFunnelEvent({ type: 'STEP_VIEWED', step: 'CONTACT', language: draft.language });
            navigate('/report/summary');
          }}
        >
          {t.common.continue}
        </Button>
      </div>
    </AppShell>
  );
}
